import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {HelpArticleService} from "../../service/api/help-article.service";
import {HelpArticleDto, HelpArticleFeedbackDto} from "../../model/dto/help-article.dto";

/**
 * Renders a help article with its content, metadata, feedback controls,
 * and related articles.
 */
@Component({
    selector: 'mvs-help-article-viewer',
    templateUrl: './help-article-viewer.component.html',
    styleUrls: ['./help-article-viewer.component.scss'],
    standalone: false
})
export class HelpArticleViewerComponent implements OnInit, OnChanges, OnDestroy {

    @Input() articleId: number;
    @Input() slug: string;
    @Output() articleNavigate = new EventEmitter<HelpArticleDto>();

    initialized = false;
    article: HelpArticleDto;
    relatedArticles: HelpArticleDto[] = [];
    isLoading = true;
    feedbackSubmitted = false;

    private subscriptions: Subscription[] = [];

    constructor(private articleService: HelpArticleService) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.loadArticle();
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['articleId'] || changes['slug']) {
            this.loadArticle();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private loadArticle(): void {
        this.isLoading = true;
        this.feedbackSubmitted = false;

        if (this.slug) {
            this.articleService.getArticleBySlug(this.slug).subscribe({
                next: article => this.onArticleLoaded(article),
                error: err => {
                    console.error('Failed to load article by slug', err);
                    this.isLoading = false;
                }
            });
        } else if (this.articleId) {
            this.articleService.getArticle(this.articleId).subscribe({
                next: article => this.onArticleLoaded(article),
                error: err => {
                    console.error('Failed to load article', err);
                    this.isLoading = false;
                }
            });
        }
    }

    private onArticleLoaded(article: HelpArticleDto): void {
        this.article = article;
        this.isLoading = false;

        // Load related articles
        this.articleService.getRelatedArticles(article.id).subscribe({
            next: related => this.relatedArticles = related,
            error: () => this.relatedArticles = []
        });
    }

    onFeedback(isHelpful: boolean): void {
        if (this.feedbackSubmitted || !this.article) return;

        const feedback: HelpArticleFeedbackDto = {
            rating: isHelpful ? 'HELPFUL' : 'NOT_HELPFUL',
            comment: ''
        };

        this.articleService.submitFeedback(this.article.id, feedback).subscribe({
            next: () => this.feedbackSubmitted = true,
            error: err => console.error('Failed to submit feedback', err)
        });
    }

    onRelatedArticleClick(article: HelpArticleDto): void {
        this.articleNavigate.emit(article);
    }
}
